import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './Icon';
import { toast } from 'sonner';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

/**
 * Lightweight, non-disruptive banner shown to signed-in users whose email is
 * not yet verified.
 *
 * In addition to the manual refresh action, the banner automatically refreshes
 * the current user when the browser regains focus or the tab becomes visible.
 * This covers the common flow where the verification link opens in another tab.
 */
export default function EmailVerificationBanner({ blocking = false }) {
  const { t } = useTranslation('auth');
  const { user, updateUser, logout } = useAuth();
  // Respect the Admin "Require Email Verification" business rule. When it is
  // OFF, no verification reminder/banner should appear anywhere.
  const { require_email_verification: requireEmailVerification } = useSiteSettings();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const refreshStatus = useCallback(async ({ silent = false } = {}) => {
    if (!requireEmailVerification || !user || !user.email || user.email_verified || user.email_verification_exempt || refreshing) return;

    setRefreshing(true);
    try {
      const res = await authAPI.me();
      updateUser(res.data);
      if (!silent) {
        if (res.data?.email_verified) {
          toast.success(t('emailVerification.verified'));
        } else {
          toast.info(t('emailVerification.notVerified'));
        }
      }
    } catch (err) {
      if (!silent) toast.error(t('emailVerification.refreshFailed'));
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, t, updateUser, user]);

  useEffect(() => {
    if (!requireEmailVerification || !user || !user.email || user.email_verified || user.email_verification_exempt) return undefined;

    const onFocus = () => refreshStatus({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshStatus({ silent: true });
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshStatus, user]);

  if (!requireEmailVerification || !user || !user.email || user.email_verified || user.email_verification_exempt || (!blocking && dismissed)) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      await authAPI.resendVerificationEmail(user.email);
      toast.success(t('emailVerification.sent'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('emailVerification.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      data-testid="email-verification-banner"
      className={`flex flex-col gap-3 rounded-lg border border-amber-300/70 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-amber-500/40 dark:bg-amber-500/10 ${blocking ? 'mx-auto mt-12 max-w-2xl shadow-lg' : ''}`}
    >
      <div className="flex items-start gap-3">
        <Icon name="mail" className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="text-sm">
          <p className="font-semibold text-amber-900 dark:text-amber-100">
            {t('emailVerification.title')}
          </p>
          <p className="mt-1 text-amber-900/80 dark:text-amber-100/80">
            {t('emailVerification.body')}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleResend}
          disabled={resending}
          data-testid="resend-verification-btn"
          className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-transparent dark:text-amber-100"
        >
          <Icon name="mail" className="me-1.5 h-3.5 w-3.5" />
          {resending ? t('emailVerification.sending') : t('emailVerification.resend')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => refreshStatus({ silent: false })}
          disabled={refreshing}
          data-testid="refresh-verification-btn"
          className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-transparent dark:text-amber-100"
        >
          <Icon name="refresh-cw" className={`me-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? t('emailVerification.checking') : t('emailVerification.verifiedAction')}
        </Button>
        {blocking ? (
          <Button size="sm" variant="ghost" onClick={logout} data-testid="verification-sign-out-btn">
            <Icon name="log-out" className="me-1.5 h-3.5 w-3.5" />
            {t('emailVerification.signOut')}
          </Button>
        ) : null}
        {!blocking ? (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setDismissed(true)}
          data-testid="dismiss-verification-btn"
          className="h-8 w-8 text-amber-900/70 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200/70 dark:hover:bg-amber-500/10"
          aria-label={t('emailVerification.dismiss')}
        >
          <Icon name="x" className="h-4 w-4" />
        </Button>
        ) : null}
      </div>
    </div>
  );
}
